import {
  AnalyticEvent,
  extrudeAdditionalInfo,
  extrudeKeyInfo,
  isMobileDevice,
  isNative,
  isWeb,
  useV2GetBundleBySlugQuery,
  useV2GetCheckoutLazyQuery,
} from "@stepapp/core";
import { AttributeType } from "@stepapp/core/apiTypes";
import { Block, Button, Divider, Loader, spacing, Typography } from "@stepapp/ui";
import { useLocalSearchParams, useRouter } from "expo-router";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { StyleSheet } from "react-native";
import {
  ErrorMessage,
  HouseAreaOutOfScopeSheet,
  InitialMyHomeForm,
  PropertyItem,
  Questions,
  ReservationNote,
  SelectAddressSheet,
  ServiceFooter,
  ServiceSceneDescription,
} from "../../../src/components";
import { TEST_OR_STAGING } from "../../../src/config/urls";
import { useAppDispatch, useAppSelector } from "../../../src/hooks";
import { useSendGTMData } from "../../../src/hooks/useSendGTMData";
import type {
  ReservationFormParams,
  SubscriptionQuestionParams,
} from "../../../src/store/draftReservation/types";
import { setHomeSize } from "../../../src/store/homeProfile";

const ServiceConfigureScreen = () => {
  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const { navigate } = useRouter();
  const sendGTMData = useSendGTMData();
  const { slug = "", editId } = useLocalSearchParams<{ slug: string; editId?: string }>();
  const properties = useAppSelector(({ user }) => user.properties);
  const floorArea = useAppSelector(({ homeProfile }) => homeProfile.floorArea);
  const { city, localizationId } = useAppSelector(({ appConfig }) => appConfig);
  const [selectAddressVisible, setSelectAddressVisible] = useState(false);
  const [newPropertyId, setNewPropertyId] = useState("");
  const [getCheckout] = useV2GetCheckoutLazyQuery();

  const { data, loading: bundleLoading } = useV2GetBundleBySlugQuery({
    variables: { localizationId, slug },
  });
  const service = data?.v2GetBundleBySlug;

  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitted },
  } = useFormContext<ReservationFormParams>();

  const [address, houseArea, price, propertyId, meta, loading, customerNote] = watch([
    "address",
    "houseArea",
    "meta.total",
    "propertyId",
    "meta",
    "meta.loading",
    "customerNote",
  ]);

  const [temporaryNote, setTemporaryNote] = useState(customerNote);

  const filteredProperties = useMemo(
    () => properties?.filter((item) => item?.localizationId === localizationId),
    [localizationId, properties],
  );

  const property = useMemo(
    () =>
      propertyId
        ? filteredProperties.find((p) => p.propertyId === propertyId)
        : filteredProperties.find((p) => p.isDefault === true) ||
          filteredProperties.find((p) => p.floorArea !== 0),
    [filteredProperties, propertyId],
  );

  const subscriptionQuestion = useMemo(() => {
    if (service?.subscriptionOptions?.length === 0) return;

    return {
      id: "subscription",
      enabled: true,
      position: -1,
      type: AttributeType.SingleChoice,
      name: t("screens.service.howOften"),
      isRequired: true,
      options: service?.subscriptionOptions?.map(
        ({ id, title, discount, description, isFeatured }) => ({
          id,
          name: title,
          price: 0,
          position: -1,
          description,
          discount,
          isFeatured,
        }),
      ),
    };
  }, [service, t]);

  const handleSendGTMData = useCallback(async () => {
    const { data: checkoutData } = await getCheckout();

    sendGTMData({ checkout: checkoutData?.v2GetCheckout, eventType: AnalyticEvent.GTM.AddToCart });
  }, [getCheckout, sendGTMData]);

  const onChangeTextBlur = useCallback(() => {
    setValue?.("customerNote", temporaryNote);
  }, [temporaryNote, setValue]);

  const onSubmit = handleSubmit(
    () => {
      !TEST_OR_STAGING && handleSendGTMData();

      return editId
        ? navigate(`/service/${slug}/summary`)
        : navigate(`/service/${slug}/date-and-address`);
    },
    async (_error) => {
      await trigger();

      if (isWeb) {
        const div = document?.querySelectorAll("#form-has-error");
        div?.[0]?.parentElement?.parentElement?.scrollIntoView({
          behavior: "smooth",
          block: isMobileDevice ? "nearest" : "center",
        });
      }
    },
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (isNative || !editId) return;

    const element = document?.getElementById(editId);

    setTimeout(
      () =>
        element?.scrollIntoView({
          behavior: "smooth",
          block: isMobileDevice ? "nearest" : "center",
        }),
      300,
    );
  }, [editId, bundleLoading]);

  useEffect(() => {
    newPropertyId && setValue("propertyId", newPropertyId);
  }, [newPropertyId, setValue]);

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: todo
  // biome-ignore lint/correctness/useExhaustiveDependencies: not required
  useEffect(() => {
    if (!(propertyId && property?.propertyId)) {
      return;
    }

    const initialKeyInfo = extrudeKeyInfo(property?.notes) || "";

    setValue("address", {
      city: property?.city || city,
      doorCode: property?.doorCode || "",
      floorArea: property?.floorArea || floorArea,
      postalOrZipCode: property?.postalOrZipCode || "",
      streetAddress1: property?.streetAddress1 || "",
      streetAddress2: property?.streetAddress2 || "",
      notes: extrudeAdditionalInfo(property?.notes) || "",
      type: property?.homeType,
    });
    setValue("houseArea", property?.floorArea || floorArea, { shouldValidate: true });
    setValue("propertyId", property?.propertyId || "");
    setValue("locationId", property?.localizationId || localizationId);
    setValue("meta", {
      ...meta,
      homeTypePlaceholder: property?.homeType || "",
      willCustomerBePresent: initialKeyInfo
        ? initialKeyInfo === t("pages.serviceDateAndAddress.iWillBeThere")
        : "deselected",
      keyInfo:
        initialKeyInfo === t("pages.serviceDateAndAddress.iWillBeThere") ? "" : initialKeyInfo,
    });
    appDispatch(setHomeSize(property?.floorArea || 0));
  }, [propertyId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleShowDetails = useCallback((showDetails: boolean) => {
    setValue("meta.detailsShown", showDetails);
  }, []);

  if (bundleLoading) {
    return (
      <Block alignCenter justifyCenter height={30}>
        <Loader />
      </Block>
    );
  }

  return (
    <Block id="container" paddingBottom={isWeb ? 10 : 0}>
      <Block marginTop={2} minHeight={3.5}>
        <Typography variant="newH2">{service?.name}</Typography>
      </Block>

      {service?.description && <ServiceSceneDescription description={service.description} />}

      {property ? (
        <Block marginTop={3}>
          <Typography variant="body">{t("screens.service.whichAddress")}</Typography>

          <ServiceSceneDescription
            description={t("screens.service.whichAddressDescription")}
            marginBottom={3}
          />

          <PropertyItem
            {...property}
            key={property?.propertyId}
            selectItem={() =>
              setValue("propertyId", property?.propertyId, { shouldValidate: isSubmitted })
            }
            selected={property?.propertyId === propertyId}
            showSecondLineAddress={false}
          />
          <Block alignStart marginBottom={1} marginTop={4}>
            <Button
              label={t("screens.service.changeAddress")}
              onPress={() => setSelectAddressVisible(true)}
              type="secondary"
              variant="small"
            />
            <ErrorMessage message={errors.propertyId?.message && t("validation.pickAddress")} />
          </Block>
        </Block>
      ) : (
        <InitialMyHomeForm
          address={address}
          errors={errors}
          houseArea={houseArea || 0}
          serviceHouseArea={service?.houseArea}
          setValue={setValue}
        />
      )}

      <Divider />

      {service ? (
        <Questions
          additionalServiceTitle={service?.additionalTitle}
          additionalServiceDescription={service?.additionalDescription}
          showCustomAdditionalDetails={service?.isAdditionalTitleAndDescriptionEnabled}
          categoryQuestions={service.categories}
          basicQuestions={service.questions}
          quantityQuestions={service.services}
          subscriptionQuestion={subscriptionQuestion as SubscriptionQuestionParams}
          categoryType={service?.categoryType}
        />
      ) : (
        <Block flex={1} />
      )}

      <Divider />

      <ReservationNote
        note={temporaryNote}
        type="edit"
        onChangeText={setTemporaryNote}
        onChangeTextBlur={onChangeTextBlur}
        categoryType={service?.categoryType}
      />

      <Divider style={styles.noteDivider} />

      <ServiceFooter
        showDetails
        disabled={loading}
        handleShowDetails={() => handleShowDetails(true)}
        label={t("common.continue")}
        loading={loading}
        onPress={() => onSubmit()}
        price={price || service?.startingPrice || ""}
      />

      <SelectAddressSheet
        currentPropertyId={propertyId || ""}
        properties={filteredProperties}
        setNewPropertyId={setNewPropertyId}
        setVisible={setSelectAddressVisible}
        visible={selectAddressVisible}
      />

      <HouseAreaOutOfScopeSheet formError={errors?.houseArea} />
    </Block>
  );
};

export default memo(ServiceConfigureScreen);

const styles = StyleSheet.create({
  noteDivider: {
    marginTop: spacing.unit * 3,
  },
});
